import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import MakeCheckPaymentDialog from './MakeCheckPaymentDialog';
import TransactionFilter, { FilterType } from './TransactionFilter';
import TransactionDateFilter from './TransactionDateFilter';
import { formatDbTimestamp } from '../../utils';

interface Transaction {
  id: number;
  store_id: number;
  direction: 'IN' | 'OUT' | 'REFUND';
  shopify_order_id: string | null;
  shopify_order_number: string | null;
  note: string | null;
  amount: string | number;
  currency: string;
  balance_after?: number | string;
  user_receiver_email: string;
  user_receiver_id: number;
  paid_at: string;
  is_manual_or_auto: string;
  manual_payment_log?: string | Record<string, any> | null;
}

const SKELETON_PLACEHOLDERS = [1, 2, 3, 4];

interface TransactionsProps {
  storeId: number;
  history: Transaction[];
  total_earned: number;
  total_refunded: number;
  total_out: number;
  currentBalance: string | number;
  payment_state?: string;
  paymentMethod?: string;
  onPaymentSuccess: (newTransaction: any) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  isLoading?: boolean;
  onApplyDateFilter?: (from?: string, to?: string) => void;
  onClearDateFilter?: () => void;
}

const Transactions: React.FC<TransactionsProps> = ({ 
  storeId, 
  history, 
  total_earned, 
  total_refunded, 
  total_out, 
  currentBalance, 
  payment_state, 
  paymentMethod, 
  onPaymentSuccess,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isLoading = false,
  onApplyDateFilter,
  onClearDateFilter
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMobileTooltip, setShowMobileTooltip] = useState(false);
  const [filters, setFilters] = useState<FilterType[]>([]);
  const [downloadingTxId, setDownloadingTxId] = useState<number | null>(null);
  const userStr = localStorage.getItem("user");
  const currentRole = userStr ? JSON.parse(userStr)?.current_role : null;
  const observerTargetRef = useRef<HTMLDivElement | null>(null);

  const isPayoutStatement = (note: string | null): boolean => {
    if (!note) return false;
    return note.toLowerCase().includes('payout statement');
  };

  const getManualLog = (tx: Transaction): Record<string, any> | null => {
    if (!tx.manual_payment_log) return null;
    let logObj: any = tx.manual_payment_log;
    if (typeof logObj === 'string') {
      try {
        logObj = JSON.parse(logObj);
      } catch {
        return null;
      }
    }
    return logObj && typeof logObj === 'object' ? logObj : null;
  };

  const isCheckPayout = (tx: Transaction): boolean => {
    return tx.direction === 'OUT' && getManualLog(tx)?.payment_method === 'check';
  };

  const formatDisplayDate = (value: string | null | undefined): string => {
    if (!value) return '';
    const parts = String(value).split('T')[0].split('-');
    if (parts.length === 3) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthIdx = parseInt(parts[1], 10) - 1;
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${months[monthIdx]} ${parseInt(parts[2], 10)}, ${parts[0]}`;
      }
    }
    return formatDbTimestamp(new Date(value), { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatementFileUrl = (tx: Transaction): string | null => {
    if (tx.manual_payment_log) {
      let logObj: any = tx.manual_payment_log;
      if (typeof logObj === 'string') {
        try {
          logObj = JSON.parse(logObj);
        } catch {
          logObj = null;
        }
      }
      if (logObj?.statement_file_url) {
        return logObj.statement_file_url;
      }
      if (logObj?.statement_file_name) {
        return `/storage/batches/stores/${logObj.statement_file_name}`;
      }
    }
    return null;
  };

  const handleDownloadStatement = async (tx: Transaction) => {
    const fileUrl = getStatementFileUrl(tx);
    if (downloadingTxId) return;

    if (!fileUrl) {
      console.warn("No statement file URL found in manual_payment_log for transaction:", tx);
      return;
    }

    setDownloadingTxId(tx.id);
    try {
      const token = localStorage.getItem("token");
      const url = `${import.meta.env.VITE_API_URL}/payments/batches/download?file_url=${encodeURIComponent(fileUrl)}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download payout statement spreadsheet");
      }

      const blob = await response.blob();
      const downloadBlobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadBlobUrl;

      const fileName = fileUrl.split("/").pop() || `Payout_Statement_Store_${tx.store_id}.xlsx`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadBlobUrl);
    } catch (err) {
      console.error("Statement download error:", err);
    } finally {
      setDownloadingTxId(null);
    }
  };

  useEffect(() => {
    if (!hasMore || isLoadingMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          onLoadMore();
        }
      },
      { rootMargin: '150px', threshold: 0.1 }
    );

    if (observerTargetRef.current) {
      observer.observe(observerTargetRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, isLoading, onLoadMore]);

  // Group transactions by date
  const groupedTransactions: Record<string, Transaction[]> = {};

  // Create a sorted copy of the history, newest first (LIFO order by paid_at)
  const sortedHistory = [...history].sort((a, b) => b.id - a.id);

  const filteredHistory = sortedHistory.filter(transaction => {
      if (filters.length === 0) return true;
      return filters.includes(transaction.direction);
  });

  const now = new Date();
  const todayStr = formatDbTimestamp(now, { month: 'numeric', day: 'numeric', year: 'numeric' });
  const currentYear = formatDbTimestamp(now, { year: 'numeric' });

  filteredHistory.forEach(transaction => {
    // Format date nicely
    let dateKey = '';

    if (formatDbTimestamp(transaction.paid_at, { month: 'numeric', day: 'numeric', year: 'numeric' }) === todayStr) {
      dateKey = 'Today';
    } else {
      const isCurrentYear = formatDbTimestamp(transaction.paid_at, { year: 'numeric' }) === currentYear;
      dateKey = formatDbTimestamp(transaction.paid_at, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: isCurrentYear ? undefined : 'numeric'
      });
    }
    
    if (!groupedTransactions[dateKey]) {
      groupedTransactions[dateKey] = [];
    }
    groupedTransactions[dateKey].push(transaction);
  });

  return (
    <div className="transactions-container">
      <div className="transactions-header-row">
        <h3 className="transactions-commission-heading">Commission Statement</h3>
        {onApplyDateFilter && onClearDateFilter && (
          <div className="transactions-header-center">
            <TransactionDateFilter 
              onApply={onApplyDateFilter} 
              onClear={onClearDateFilter}
              disabled={isLoading}
            />
          </div>
        )}
        <div className="transactions-header-right">
          {currentRole === 1 && (() => {
            let disableReason = '';
            if (payment_state === 'FAILED') {
              disableReason = 'Unable to make payment at the moment.';
            } else if (paymentMethod !== 'check') {
              disableReason = 'Payment details not set!';
            } else if (Number(currentBalance) < 0) {
              disableReason = 'Outstanding balance is negative.';
            } else if (Number(currentBalance) === 0) {
              disableReason = 'Outstanding balance is 0';
            }

            return (
              <div 
                className="payouts-btn-wrapper transactions-header-btn"
                onClick={() => {
                  if (disableReason) {
                    setShowMobileTooltip(true);
                    setTimeout(() => setShowMobileTooltip(false), 3000);
                  }
                }}
              >
               
                {disableReason && (
                  <span className={`payouts-tip ${showMobileTooltip ? 'mobile-visible' : ''}`}>
                    {disableReason}
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="transactions-stats-row">
        <TransactionFilter currentFilters={filters} onFilterChange={setFilters} />
        {payment_state === 'FAILED' ? (
          currentRole === 1 ? (
            <div className="payment-reconciliation-issue">
              Payment reconciliation issue
            </div>
          ) : null
        ) : (
          <>
          {Number(total_earned) > 0 && (
            <div className="stat-item">
              <span className="stat-label">Total Earned</span>
              <span className="stat-value in">{Number(total_earned) < 0 ? '-' : ''}${Math.abs(Number(total_earned)).toFixed(2)}</span>
            </div>)}
            {Number(total_out) > 0 && (
            <div className="stat-item">
              <span className="stat-label">Total Paid Out</span>
              <span className="stat-value out">{Number(total_out) < 0 ? '-' : ''}${Math.abs(Number(total_out)).toFixed(2)}</span>
            </div>)}
            {Number(total_refunded) > 0 && (
              <div className="stat-item">
                <span className="stat-label">Total Refunded</span>
                <span className="stat-value refund">{Number(total_refunded) < 0 ? '-' : ''}${Math.abs(Number(total_refunded)).toFixed(2)}</span>
              </div>
            )}
            <div className="stat-item">
              <span className="stat-label">Available Balance</span>
              <span className="stat-value">
                {Number(currentBalance) < 0 ? '-' : ''}${Math.abs(Number(currentBalance)).toFixed(2)}
              </span>
            </div>
          </>
        )}
      </div>

      <div className="transactions-list">
        {isLoading ? (
          <div className="skeleton-transactions-wrapper">
            {SKELETON_PLACEHOLDERS.map((placeholderIndex) => (
              <div key={placeholderIndex} className="transaction-item skeleton-item">
                <div className="transaction-info">
                  <div className="skeleton-line skeleton-title"></div>
                  <div className="skeleton-line skeleton-meta"></div>
                </div>
                <div className="transaction-amounts-wrapper">
                  <div className="skeleton-line skeleton-amount"></div>
                  <div className="skeleton-line skeleton-balance"></div>
                </div>
              </div>
            ))}
          </div>
        ) : Object.keys(groupedTransactions).length === 0 ? (
          <p>No transactions found.</p>
        ) : (
          <>
            {Object.keys(groupedTransactions).map((dateKey, index) => (
              <div key={dateKey} className="transaction-date-group">
              <div className="transaction-date-header">
                <span>{dateKey}</span>
                {index === 0 && (
                  <div className="transaction-amounts-wrapper transaction-amounts-header">
                    <div className="transaction-amount-header">Amount</div>
                    <div className="transaction-balance-header">Balance</div>
                  </div>
                )}
              </div>
              {groupedTransactions[dateKey].map(transaction => {
                const checkPayout = isCheckPayout(transaction);
                const log = checkPayout ? getManualLog(transaction) : null;
                const checkIssuedDateStr = formatDisplayDate(log?.check_issued_date);
                const startDateStr = formatDisplayDate(log?.start_date);
                const endDateStr = formatDisplayDate(log?.end_date);
                return (
                <div key={transaction.id} className="transaction-item">
                  <div className="transaction-info">
                    <span className="transaction-id">
                      {transaction.direction}
                      <span className="transaction-id-bracket">
                        {transaction.shopify_order_number
                          ? ` (#${transaction.shopify_order_number})`
                          : checkPayout
                            ? ' (Payout)'
                            : ''}
                      </span>
                    </span>
                    {checkPayout ? (
                      <div className="transaction-payout-block">
                        <span className="transaction-payout-line">
                          Paid by check{checkIssuedDateStr ? ` · Check dated ${checkIssuedDateStr}` : ''}
                        </span>
                        <span className="transaction-payout-line">
                          {startDateStr && endDateStr ? `Covers earnings from ${startDateStr} to ${endDateStr} · ` : ''}
                          <span
                            className={`payout-statement-link ${downloadingTxId === transaction.id ? "downloading" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadStatement(transaction);
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <span className="payout-statement-text">Payout statement</span>
                            <DownloadIcon className="payout-statement-icon" />
                          </span>
                        </span>
                      </div>
                    ) : (
                      <span className="transaction-meta">
                        {formatDbTimestamp(transaction.paid_at, { hour: '2-digit', minute: '2-digit' })}
                        {transaction.note && (
                          isPayoutStatement(transaction.note) ? (
                            <>
                              {" • "}
                              <span
                                className={`payout-statement-link ${downloadingTxId === transaction.id ? "downloading" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadStatement(transaction);
                                }}
                                role="button"
                                tabIndex={0}
                              >
                                <span className="payout-statement-text">{transaction.note}</span>
                                <DownloadIcon className="payout-statement-icon" />
                              </span>
                            </>
                          ) : (
                            <span className="refund-color"> • {transaction.note}</span>
                          )
                        )}
                      </span>
                    )}
                  </div>
                  <div className="transaction-amounts-wrapper">
                    <div className={`transaction-amount ${transaction.direction.toLowerCase()}`}>
                      {transaction.direction === 'IN' ? '+' : '-'}${Number(transaction.amount).toFixed(2)}
                    </div>
                    {transaction.balance_after != null && (
                      <div className="transaction-balance">
                        {Number(transaction.balance_after) < 0 ? '-' : ''}${Math.abs(Number(transaction.balance_after)).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          ))}
          <div ref={observerTargetRef} className="infinite-scroll-trigger" />
          {isLoadingMore && (
            <div className="skeleton-transactions-wrapper">
              {SKELETON_PLACEHOLDERS.map((placeholderIndex) => (
                <div key={placeholderIndex} className="transaction-item skeleton-item">
                  <div className="transaction-info">
                    <div className="skeleton-line skeleton-title"></div>
                    <div className="skeleton-line skeleton-meta"></div>
                  </div>
                  <div className="transaction-amounts-wrapper">
                    <div className="skeleton-line skeleton-amount"></div>
                    <div className="skeleton-line skeleton-balance"></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {hasMore && !isLoadingMore && (
            <div className="load-more-btn-wrapper">
              <Button 
                variant="outlined" 
                onClick={onLoadMore}
                className="load-more-btn"
              >
                Load More
              </Button>
            </div>
          )}
        </>
        )}
      </div>

      {currentRole === 1 && (
        <MakeCheckPaymentDialog
          open={isModalOpen}
          storeId={storeId}
          onClose={() => setIsModalOpen(false)}
          onPaymentSuccess={onPaymentSuccess}
        />
      )}
    </div>
  );
};

export default Transactions;
